'use client';

import { Fragment, useMemo, useState, type CSSProperties } from 'react';
import { usePathname } from 'next/navigation';
import { useTranslation } from 'react-i18next';
import Link from 'next/link';
import { findWikiPath, useWikiSource, type WikiTreeNode } from '@akademi/site-builder-runtime';
import { AdapterPlaceholder } from '../_shared/AdapterPlaceholder';
import { useAsyncResource } from '../_shared/useAsyncResource';
import { BlockRoot } from '../_shared/BlockRoot';

/**
 * Sidebar wiki tree-nav block. Drops into the {@code sidebar-tree} slot
 * of the {@code wiki-detail} collection layout (or anywhere else the
 * operator wants a structured documentation nav).
 *
 * <p>The block reads the current path from {@link usePathname} to mark
 * the active page and auto-expand its ancestor chain — clicking a node
 * deeper in the tree feels like a real router-aware sidebar even though
 * the block is presentational + adapter-fed.
 *
 * <h3>Data flow</h3>
 * Uses the host-provided {@code WikiSource} adapter (
 * {@link useWikiSource}). The portal renderer wires the adapter to
 * {@code /v1/public/{tenantId}/wiki/page/...} via its host shell, so the
 * block itself stays HTTP-agnostic and works in dev/storybook against a
 * stub adapter.
 *
 * <h3>Props</h3>
 * <ul>
 *   <li>{@code title} — section heading. Defaults to the catalogue's
 *       "on this site" wording.</li>
 *   <li>{@code rootSlug} — pin the visible tree to a subtree. Optional;
 *       default is the whole tenant tree.</li>
 *   <li>{@code initiallyExpanded} — {@code 'all' | 'ancestors'} (default
 *       {@code 'ancestors'}). {@code 'all'} expands every node; {@code
 *       'ancestors'} expands only the active page's ancestor chain.</li>
 *   <li>{@code pageHrefTemplate} — link template. {@code {slug}} is
 *       replaced per node. Default {@code /wiki/{slug}}.</li>
 * </ul>
 */
export interface WikiTreeNavProps {
  title?: string;
  rootSlug?: string;
  initiallyExpanded?: 'all' | 'ancestors';
  pageHrefTemplate?: string;
}

function fillTemplate(tpl: string, slug: string): string {
  return tpl.replace('{slug}', slug);
}

function findNode(tree: WikiTreeNode[], slug: string): WikiTreeNode | null {
  for (const node of tree) {
    if (node.slug === slug) return node;
    const inChild = findNode(node.children, slug);
    if (inChild) return inChild;
  }
  return null;
}

function extractCurrentSlug(pathname: string, template: string): string | null {
  // Template like '/wiki/{slug}'. Build a prefix and trim.
  const placeholderIdx = template.indexOf('{slug}');
  if (placeholderIdx < 0) return null;
  const prefix = template.slice(0, placeholderIdx);
  const suffix = template.slice(placeholderIdx + '{slug}'.length);
  if (!pathname.startsWith(prefix)) return null;
  let rest = pathname.slice(prefix.length);
  if (suffix.length > 0 && rest.endsWith(suffix)) {
    rest = rest.slice(0, rest.length - suffix.length);
  }
  rest = rest.replace(/^\/+/, '').replace(/\/+$/, '');
  return rest.length === 0 ? null : rest;
}

export function WikiTreeNav({
  title,
  rootSlug,
  initiallyExpanded = 'ancestors',
  pageHrefTemplate = '/wiki/{slug}',
}: WikiTreeNavProps) {
  const { t } = useTranslation('site-builder-blocks');
  const heading = title ?? t('discovery.wikiNav.title');
  const wiki = useWikiSource();
  const pathname = usePathname() ?? '';

  const treeState = useAsyncResource<WikiTreeNode[]>(async () => {
    if (!wiki) return null;
    const tree = await wiki.getTree();
    return tree.length > 0 ? tree : null;
  }, [wiki]);

  const currentSlug = useMemo(
    () => extractCurrentSlug(pathname, pageHrefTemplate),
    [pathname, pageHrefTemplate],
  );

  const tree: WikiTreeNode[] = treeState.kind === 'loaded' ? treeState.data : [];

  const visibleTree: WikiTreeNode[] = useMemo(() => {
    if (!rootSlug) return tree;
    const root = findNode(tree, rootSlug);
    return root?.children ?? [];
  }, [tree, rootSlug]);

  const ancestorSlugs: Set<string> = useMemo(() => {
    if (!currentSlug) return new Set<string>();
    const chain = findWikiPath(tree, currentSlug);
    return new Set(chain.map((n) => n.slug));
  }, [tree, currentSlug]);

  if (!wiki) {
    return (
      <BlockRoot as="aside" style={asideStyle}>
        <AdapterPlaceholder blockType="WikiTreeNav" adapterName="WikiSource" />
      </BlockRoot>
    );
  }
  if (treeState.kind !== 'loaded' || visibleTree.length === 0) {
    return null;
  }

  return (
    <BlockRoot as="aside" style={asideStyle}>
      {heading && <h3 style={titleStyle}>{heading}</h3>}
      <ul style={listStyle}>
        {visibleTree.map((node) => (
          <TreeNodeRow
            key={node.slug}
            node={node}
            depth={0}
            currentSlug={currentSlug}
            ancestorSlugs={ancestorSlugs}
            template={pageHrefTemplate}
            mode={initiallyExpanded}
          />
        ))}
      </ul>
    </BlockRoot>
  );
}

interface TreeNodeRowProps {
  node: WikiTreeNode;
  depth: number;
  currentSlug: string | null;
  ancestorSlugs: Set<string>;
  template: string;
  mode: 'all' | 'ancestors';
}

function TreeNodeRow({
  node,
  depth,
  currentSlug,
  ancestorSlugs,
  template,
  mode,
}: TreeNodeRowProps) {
  const { t } = useTranslation('site-builder-blocks');
  const isCurrent = currentSlug === node.slug;
  const isAncestor = ancestorSlugs.has(node.slug);
  const hasChildren = node.children.length > 0;
  const initialOpen = mode === 'all' || isAncestor || isCurrent;
  const [open, setOpen] = useState<boolean>(initialOpen);

  return (
    <li style={{ ...rowStyle, paddingLeft: depth * 12 }}>
      <div style={rowInnerStyle}>
        {hasChildren ? (
          <button
            type="button"
            aria-expanded={open}
            aria-label={open ? t('discovery.wikiNav.collapse') : t('discovery.wikiNav.expand')}
            onClick={() => setOpen((v) => !v)}
            style={toggleButtonStyle}
          >
            <Caret open={open} />
          </button>
        ) : (
          <span style={toggleSpacerStyle} aria-hidden="true" />
        )}
        <Link
          href={fillTemplate(template, node.slug)}
          aria-current={isCurrent ? 'page' : undefined}
          style={{
            ...linkStyle,
            fontWeight: isCurrent ? 600 : isAncestor ? 500 : 400,
            color: isCurrent
              ? 'var(--color-text-brand, var(--color-brand-700, #2b3eb0))'
              : 'var(--color-text-primary, #18181b)',
          }}
        >
          {node.title}
        </Link>
      </div>
      {hasChildren && open && (
        <ul style={listStyle}>
          {node.children.map((child) => (
            <Fragment key={child.slug}>
              <TreeNodeRow
                node={child}
                depth={depth + 1}
                currentSlug={currentSlug}
                ancestorSlugs={ancestorSlugs}
                template={template}
                mode={mode}
              />
            </Fragment>
          ))}
        </ul>
      )}
    </li>
  );
}

function Caret({ open }: { open: boolean }) {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 10 10"
      style={{
        transition: 'transform 120ms ease-out',
        transform: open ? 'rotate(90deg)' : 'none',
      }}
      aria-hidden="true"
    >
      <path d="M3 1 L7 5 L3 9" fill="none" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

const asideStyle: CSSProperties = {
  background: 'var(--color-surface-card, #fff)',
  borderRight: '1px solid var(--color-border-subtle, rgba(0,0,0,0.08))',
  padding: 'var(--space-4, 16px)',
};

const titleStyle: CSSProperties = {
  margin: '0 0 var(--space-3, 12px)',
  fontSize: '13px',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 'var(--font-tracking-wider, 0.06em)',
  color: 'var(--color-text-secondary, #71717a)',
};

const listStyle: CSSProperties = {
  listStyle: 'none',
  padding: 0,
  margin: 0,
};

const rowStyle: CSSProperties = {
  margin: 0,
  padding: 'var(--space-0-5, 2px) 0',
};

const rowInnerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 4,
};

const toggleButtonStyle: CSSProperties = {
  width: 18,
  height: 18,
  padding: 0,
  background: 'transparent',
  border: 0,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'var(--color-text-secondary, #71717a)',
};

const toggleSpacerStyle: CSSProperties = {
  display: 'inline-block',
  width: 18,
  height: 18,
};

const linkStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm, 14px)',
  textDecoration: 'none',
  display: 'inline-block',
  padding: 'var(--space-0-5, 2px) var(--space-1, 4px)',
  borderRadius: 'var(--radius-sm, 4px)',
};
